import type { ReactNode } from 'react';
import { CardHeader, type CardHeaderDragHandle, type CardHeaderTabs } from '../CardHeader/CardHeader';
import { LoadingState } from '../LoadingState/LoadingState';
import styles from './Card.module.css';

/**
 * Figma: `Card` (488:14487) — `State=Default` (415:47173), `State=Loading` and
 * `State=2 charts` (918:35576), the last added 2026-09-16 and reached here by
 * passing `chart2` rather than by a `state` value. See that prop.
 *
 * Original note follows: `State=Default` (415:47173) and `State=Loading`
 * (904:35112), both 586x380, no annotation.
 *
 * The single-variant set the 2026-09 update introduced has grown its second
 * variant, and the axis was renamed `Property 1` -> `State` in the same pass —
 * so the scaffolding that "carries no information" now does, and `state` is a
 * real prop.
 *
 * `State=Loading` is byte-identical to `State=Default` but for one thing: the
 * `Chart` slot centres a `Loading State` instance (`Pattern=Orbit`) with its
 * label and timer layers switched off and its cells recoloured blue. Everything
 * else — the box, the header and its whole actions row, the wrapper, the gaps —
 * is untouched, which is the point: a card that is loading is the same card.
 *
 * The chart host. A single `surface/card` panel holds a `Card Header` instance
 * and three SLOT nodes — `KPI`, `Chart` and `Legend`.
 *
 * They are no longer all 16px apart. The 2026-09-16 update introduced a
 * `KPI+Chart Wrapper` frame around the first two, on its own `space/24` gap,
 * leaving the card's own `space/16` to separate the header, that wrapper and the
 * legend. So the rhythm is 16 / 24 / 16 down the card, not 16 throughout — the
 * figure and its plot bind together and the legend stays a step apart.
 *
 * The 2026-08 update flattened this. It used to be a `surface/secondary` shell
 * wrapping a white `Chart Wrapper` panel that carried its own 12px inset and
 * 24px gap; that inner panel is gone, the whole card is white, and all four
 * children now sit directly on the card's own 16px padding and gap.
 *
 * Those three are real Figma `SLOT` component properties, so they arrive here as
 * `ReactNode` slots. Each renders only when filled: an empty slot must not leave
 * a gap behind it. All three are FIXED vertically on the canvas — 36 / 224 / 12
 * since the 2026-09-16 update — but only the `Chart` figure is a constraint, and
 * it is carried by `ChartFrame`'s own `height` default rather than from here. A
 * KPI row and a legend size themselves; see `.chartSlot`.
 *
 * The header is a fixed instance in Figma, not a slot, so `Card` renders it and
 * forwards its content rather than taking a `header` node. The instance sits at
 * `Supporting text=true, Actions=false, State=Default`.
 *
 * `Card Header` was reworked in 2026-09 — a dashed help underline, a leading
 * `globe` trigger, and no supporting-text line any more, which is why `Card`
 * no longer takes `supportingText`. See `CardHeader`.
 *
 * The 2026-09-14 pass filled in its actions row: a `Size=sm` `Tabs` switcher,
 * an "AI summary" Ghost Colour button and a `more` Ghost Grey icon button, each
 * behind its own boolean. The card's own box is untouched by all of this — same
 * `space/16` padding, same `radius/xl`, same INSIDE `border/primary` stroke. The
 * frame is 586x380 now, down from 440: the header lost 8 going back to a 20-tall
 * row and the slots were redrawn 36 / 224 / 12 against the old 34 / 278 / 20.
 * Those are placeholder sizes on the canvas, not constraints — see `.chartSlot`.
 * The three controls are forwarded to `CardHeader` rather than reachable only by
 * building a header by hand.
 *
 * Figma pins the frame to 586 wide, but a card is a dashboard tile — it spans
 * whatever column it lands in. Same call `TableHeader` and `Pagination` made, and
 * the same reason: a FIXED width in the file describes the artboard, not the
 * component.
 *
 * ---
 *
 * **`Cards/Draggable Card` (996:33372) is this component, not a second one.**
 *
 * The Custom Reports page (990:44858) introduced a parallel set with a third
 * variant axis, `Type = Default | Dragging`. Read side by side with `Card`
 * (488:14487) the two are the same component: identical box, identical
 * `space/16` rhythm, the same `KPI` / `Chart 1` / `Chart 2` / `Legend` slots,
 * and the same `State` and `Size` axes with the same 586x380 / 283x84 / 283x80
 * geometry. Exactly two things differ, and both are one line here:
 *
 *   the header     `Card Header` at `Type=Draggable` instead of `Type=Default`
 *                  — reached by passing `dragHandle` / `onRemove` through.
 *   `Type=Dragging` `border/focus` in place of `border/primary`, plus
 *                  `shadow/lg`. That is the `dragging` prop and `.dragging`.
 *
 * Mirroring the file's two sets with two React components would mean a second
 * copy of the loading, empty, two-chart and `Size=sm` logic — four behaviours
 * that have each been re-read from Figma more than once, and that a report
 * widget needs exactly as much as a dashboard card does. Two copies is two
 * places for "what does a loading card look like" to drift, which is the
 * argument this codebase makes everywhere else. One component, three props.
 *
 * Recorded for the designer in plan.md §7 rather than silently: the sets are
 * duplicates and would be better as one with a `Type` axis.
 */
type CardCommonProps = {
  /** Figma `Card Header` → `Text`. */
  title: string;
  /**
   * Figma `State`. `loading` replaces the whole body with a centred loader and
   * draws neither the KPI nor the legend, even when they are passed — the file's
   * slots are empty in both variants and say nothing either way, so this is a
   * product decision rather than a reading of the design: a card that has no
   * series yet has no figure to headline it and no key to explain.
   *
   * `empty` (940:37880) makes the same swap for the same reason, with a line of
   * text where the loader stands: the query came back with nothing, so there is
   * no figure and no key here either. The two are deliberately the same shape —
   * a card with nothing to show looks the same whether it is still waiting or
   * came back empty-handed — and the card keeps its height through both, so a
   * grid row does not resize as one settles.
   *
   * The header stays in all three. It names the card, and its controls are the
   * way out of a load that is taking too long.
   */
  state?: 'default' | 'loading' | 'empty';
    /** Figma `Card Header` → `Help icon`. Omit for no help icon. */
  helpText?: string;
  /** Second line in the help tooltip. */
  helpSupportingText?: string;
  /** Figma `Card Header` → `Tab` — the `Bar` / `Line` chart-type switcher. */
  tabs?: CardHeaderTabs;
  /** Figma `Card Header` → `AI summary`. Provide a handler to render it. */
  onAiSummary?: () => void;
  /** Label for that button. Figma draws "AI summary". */
  aiSummaryLabel?: string;
  /** Figma `Card Header` → `More`. Provide a handler to render it. */
  onMore?: () => void;
  /** Accessible name for the more button. Not in Figma. */
  moreLabel?: string;
  /**
   * Figma `Cards/Draggable Card` → `Card Header` `Type=Draggable` — the
   * `ri:draggable` handle. Provide it to render one.
   */
  dragHandle?: CardHeaderDragHandle;
  /**
   * Figma `Cards/Draggable Card` → `Card Header` `Type=Draggable` — the
   * trailing `close`. Provide a handler to take the card off the page.
   */
  onRemove?: () => void;
  /** Accessible name for that button. Not in Figma. */
  removeLabel?: string;
  /**
   * Figma `Cards/Draggable Card` `Type=Dragging` (996:33834) — the card while
   * it is picked up: `border/focus` in place of `border/primary`, and
   * `shadow/lg`. Nothing else about the card changes.
   */
  dragging?: boolean;
  /** Bumped on a magnetic catch — see `CardHeader.flash`. */
  flash?: number;
  /** Figma `Card Header` → `Actions Wrapper` — extra trailing content. */
  actions?: ReactNode;
  /**
   * The line drawn in the middle of `state="empty"`. Figma's own text
   * (940:37933) reads "No data available", which is the default; a card can
   * override it where it can say something more useful about *why* — "No
   * moments in this period" beats a generic blank.
   */
  emptyLabel?: ReactNode;
  className?: string;
};

/**
 * Figma `Size=md` — the 586x380 chart tile every dashboard card has been so
 * far. The default, because it is what seven of the file's nine dashboard
 * frames draw and what every existing caller already means.
 */
export type CardMdProps = CardCommonProps & {
  size?: 'md';
  /** Figma SLOT `KPI` — the figure row above the plot. */
  kpi?: ReactNode;
  /** Figma SLOT `Chart` — the plot itself. */
  chart?: ReactNode;
  /**
   * Figma SLOT `Chart2` (918:35612) — a second plot under the first.
   *
   * Passing it *is* the `State=2 charts` variant (918:35576); there is no
   * matching value on the `state` prop, because the variant is entirely defined
   * by whether a second plot exists and a flag that could disagree with the
   * content would be a bug waiting to happen.
   *
   * The file splits the 224 chart area into two FIXED 106 slots `space/12`
   * apart. Here they share the area equally instead, for the reason
   * `.chartSlot` already gives: a card stretches to the tallest in its grid row,
   * and 106 is the height of the artboard rather than a constraint.
   */
  chart2?: ReactNode;
  /** Figma SLOT `Legend` — the series key below the plot. */
  legend?: ReactNode;
};

/**
 * Figma `Size=sm` — `State=Default` (964:11277), `State=Loading` (964:11420),
 * `State=Empty` (964:11483). 283x84, 283x84 and 283x80.
 *
 * A headline figure with no plot under it: the same box, the same header and
 * the same 16px rhythm, holding one `_KPI Text` where the md card holds the
 * `KPI+Chart Wrapper`. The `Chart`, `Chart 2` and `Legend` slots are not in the
 * variant at all, which is why they are `never` here rather than ignored — a
 * caller passing a plot to a card that cannot draw one should not have to find
 * that out by looking at the screen.
 *
 * The figure is a `KPIText`, not a `KPI`: the row of label-over-value metrics
 * needs labels, and here the card's own header is the label. Overview's four
 * top cards are the whole of its use so far (`972:20768`–`972:20771`).
 *
 * **Its height is a measurement, not a floor.** The md card pins its swapped
 * body to the `Chart` slot's 224 so a settling card does not shrink its grid
 * row; an sm card has no chart slot to pin to and is 84 tall because 16 + 20 +
 * 16 + 16 + 16 is 84. The empty variant is 80 for the same arithmetic — a
 * 12-tall line where the 16-tall figure stood — and that 4px step is the one
 * place the file lets a card change height with its state.
 */
export type CardSmProps = CardCommonProps & {
  size: 'sm';
  /** Figma `_KPI Text` — the single figure. Pass a `KPIText`. */
  kpi?: ReactNode;
  chart?: never;
  chart2?: never;
  legend?: never;
};

export type CardProps = CardMdProps | CardSmProps;

export function Card({
  title,
  size = 'md',
  state = 'default',
  helpText,
  helpSupportingText,
  tabs,
  onAiSummary,
  aiSummaryLabel,
  onMore,
  moreLabel,
  dragHandle,
  onRemove,
  removeLabel,
  dragging = false,
  flash,
  actions,
  emptyLabel = 'No data available',
  kpi,
  chart,
  chart2,
  legend,
  className,
}: CardProps) {
  const loading = state === 'loading';
  const empty = state === 'empty';
  /* Both replace the body wholesale; only what stands in the middle differs. */
  const bodyReplaced = loading || empty;
  const small = size === 'sm';

  return (
    <section
      className={[styles.card, small && styles.sm, dragging && styles.dragging, className]
        .filter(Boolean)
        .join(' ')}
      /*
       * Not in Figma — the file draws no accessible semantics. The body is
       * swapped rather than progressively filled, so the region has to say it is
       * mid-update or a screen reader is told only that content vanished.
       */
      aria-busy={loading || undefined}
    >
      <CardHeader
        title={title}
        helpText={helpText}
        helpSupportingText={helpSupportingText}
        tabs={tabs}
        onAiSummary={onAiSummary}
        aiSummaryLabel={aiSummaryLabel}
        onMore={onMore}
        moreLabel={moreLabel}
        dragHandle={dragHandle}
        flash={flash}
        onRemove={onRemove}
        removeLabel={removeLabel}
        actions={actions}
      />

      {bodyReplaced ? (
        /*
          Figma centres both the loader (904:35148) and the empty line
          (940:37933) in the `Chart` slot, which is the box each stands in for —
          so the body keeps that slot's height as a floor and grows past it with
          the card. `Pattern=Orbit` and the two hidden layers are the loading
          instance's own overrides, read straight off 904:35148.
        */
        <div className={styles.replacedBody}>
          {loading ? (
            <LoadingState pattern="orbit" tone="accent" showLabel={false} showElapsed={false} />
          ) : (
            <p className={styles.emptyMessage}>{emptyLabel}</p>
          )}
        </div>
      ) : small ? (
        /*
         * Figma `State=Default, Size=sm` (964:11277) — the `_KPI Text` is a
         * direct child of the card on its own 16px gap, with no `KPI+Chart
         * Wrapper` around it. There is nothing for that wrapper's 24px gap to
         * separate, and wrapping anyway would put a 24px band inside an 84px
         * card.
         */
        kpi !== undefined && (
          <div className={[styles.slot, styles.smFigure].filter(Boolean).join(' ')}>{kpi}</div>
        )
      ) : (
        <>
          {/*
            Figma `KPI+Chart Wrapper` — the figure and its plot, `space/24`
            apart, inside the card's own `space/16` rhythm. Drawn only when it
            would hold something, so a legend-only card does not carry an empty
            24px band.
          */}
          {(kpi !== undefined || chart !== undefined) && (
            <div className={styles.kpiChartWrapper}>
              {kpi !== undefined && <div className={styles.slot}>{kpi}</div>}
              {chart !== undefined && chart2 !== undefined ? (
                /*
                 * Figma `Frame 1` (918:35613) — the chart area as a VERTICAL
                 * stack of two plots `space/12` apart. It takes the leftover
                 * height the single slot would have taken, and the two plots
                 * split it.
                 */
                <div className={styles.chartPair}>
                  <div className={[styles.slot, styles.chartSlot].join(' ')}>{chart}</div>
                  <div className={[styles.slot, styles.chartSlot].join(' ')}>{chart2}</div>
                </div>
              ) : (
                chart !== undefined && (
                  <div className={[styles.slot, styles.chartSlot].join(' ')}>{chart}</div>
                )
              )}
            </div>
          )}

          {legend !== undefined && <div className={styles.slot}>{legend}</div>}
        </>
      )}
    </section>
  );
}
